/**
 * A field-level diff of two JSON definitions (authoring-ui.md AU-8): every leaf that was
 * added, removed or changed, as `path: old → new`. Objects are compared member by member;
 * arrays of objects that each carry a unique string `key` or `id` (a Workflow's steps, a
 * glossary's terms, a checklist's items) are matched by it, other arrays of objects by
 * position; arrays of plain values are one leaf, so a tool list reads as one change, not
 * as shifted positions.
 *
 * Used by the review page and by the authoring forms' definition preview, so a proposal
 * reads the same before and after it is sent.
 */

export type FieldChangeKind = "added" | "removed" | "changed";

export interface FieldChange {
  /** Dotted path; keyed array members as `steps[gather]`, others as `items[2]`. */
  readonly path: string;
  readonly kind: FieldChangeKind;
  /** `undefined` when added. */
  readonly before: unknown;
  /** `undefined` when removed. */
  readonly after: unknown;
}

export function fieldDiff(before: unknown, after: unknown): readonly FieldChange[] {
  const changes: FieldChange[] = [];
  compare("", before, after, changes);
  return changes;
}

function compare(path: string, before: unknown, after: unknown, changes: FieldChange[]): void {
  if (sameValue(before, after)) {
    return;
  }
  if (before === undefined) {
    changes.push({ path, kind: "added", before, after });
    return;
  }
  if (after === undefined) {
    changes.push({ path, kind: "removed", before, after });
    return;
  }
  if (isPlainObject(before) && isPlainObject(after)) {
    const members = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
    for (const member of members) {
      compare(join(path, member), before[member], after[member], changes);
    }
    return;
  }
  if (Array.isArray(before) && Array.isArray(after) && isObjectArray(before) && isObjectArray(after) && before.length + after.length > 0) {
    compareArrays(path, before, after, changes);
    return;
  }
  changes.push({ path, kind: "changed", before, after });
}

function compareArrays(path: string, before: readonly Record<string, unknown>[], after: readonly Record<string, unknown>[], changes: FieldChange[]): void {
  const identity = identityMember(before, after);
  if (identity !== null) {
    const beforeByKey = new Map(before.map((member) => [String(member[identity]), member]));
    const afterKeys = new Set(after.map((member) => String(member[identity])));
    for (const member of after) {
      const key = String(member[identity]);
      compare(`${path}[${key}]`, beforeByKey.get(key), member, changes);
    }
    for (const [key, member] of beforeByKey) {
      if (!afterKeys.has(key)) {
        compare(`${path}[${key}]`, member, undefined, changes);
      }
    }
    return;
  }
  const length = Math.max(before.length, after.length);
  for (let index = 0; index < length; index += 1) {
    compare(`${path}[${String(index)}]`, before[index], after[index], changes);
  }
}

function join(path: string, member: string): string {
  return path === "" ? member : `${path}.${member}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** An array of objects; an empty one too, so a list that fills up is compared entry by entry. */
function isObjectArray(value: readonly unknown[]): value is readonly Record<string, unknown>[] {
  return value.every(isPlainObject);
}

/** The member both sides' entries are identified by, `key` or `id`, if each is a unique string. */
function identityMember(before: readonly Record<string, unknown>[], after: readonly Record<string, unknown>[]): "key" | "id" | null {
  const unique = (members: readonly Record<string, unknown>[], member: string) => {
    const values = members.map((entry) => entry[member]);
    return values.every((value) => typeof value === "string") && new Set(values).size === values.length;
  };
  for (const member of ["key", "id"] as const) {
    if (unique(before, member) && unique(after, member) && before.length + after.length > 0 && [...before, ...after].some((entry) => member in entry)) {
      return member;
    }
  }
  return null;
}

/** Deep equality of JSON values; object member order does not matter. */
export function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((member, index) => sameValue(member, right[index]));
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left).filter((key) => left[key] !== undefined);
    const rightKeys = Object.keys(right).filter((key) => right[key] !== undefined);
    return leftKeys.length === rightKeys.length && leftKeys.every((key) => sameValue(left[key], right[key]));
  }
  return false;
}
